import { useState } from 'react';
import ProductCartCounter from './ProductCartCounter';
import ProductImageCarousel, { getProductImages } from './ProductImageCarousel';
import { isValidProductUrl } from '../utils/segmentation';
import { stripHtml } from '../utils/sanitizeHtml';
import './RecommendedProductCard.css';

function formatPrice(amount, priceLabel) {
  if (amount != null && amount !== '') {
    const n = Number(amount);
    if (!Number.isNaN(n)) return `₹${Math.round(n)}`;
  }
  if (priceLabel) {
    const match = String(priceLabel).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    if (match) return `₹${Math.round(Number(match[1]))}`;
    return priceLabel;
  }
  return '—';
}

function formatRoundedAmount(value) {
  const n = Number(value);
  if (value == null || value === '' || Number.isNaN(n)) return null;
  return Math.round(n);
}

function renderStars(rating) {
  const value = Number(rating);
  if (Number.isNaN(value) || value <= 0) return null;
  const full = Math.round(value);
  return '★'.repeat(Math.min(full, 5)) + '☆'.repeat(Math.max(0, 5 - Math.min(full, 5)));
}

function severityClass(level) {
  const key = String(level || '').toLowerCase();
  if (key === 'high') return 'badge-high';
  if (key === 'moderate') return 'badge-moderate';
  if (key === 'low') return 'badge-low';
  return 'badge-neutral';
}

function priorityLabel(level) {
  const top = String(level || '').toLowerCase();
  if (!top || top === 'good') return null;
  return `${top.charAt(0).toUpperCase()}${top.slice(1)} priority`;
}

function cleanAiReason(text) {
  return String(text || '')
    .replace(/\(score[:\s]*[\d.]+\)/gi, '')
    .replace(/score\s*[\d.]+/gi, '')
    .replace(/\b(high|moderate|low)\s+severity\b/gi, '$1 priority')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function uniqueList(items) {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const value = String(item || '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

export default function RecommendedProductCard({
  product,
  productId,
  cartQuantity = 0,
  showAddToCart = true,
  showProductLink = true,
  onAddToCart,
  onViewProduct,
  storeTrust = [],
  cardRef,
  className = '',
}) {
  const imageUrls = getProductImages(product);
  const name = product.product_name || product.name || 'Product';
  const levels = product.severity_levels || [];
  const ingredients = uniqueList(product.key_actives || product.matched_ingredients || []);
  const matchedIngredients = uniqueList(product.matched_ingredients || product.key_actives || []);
  const link = product.product_link || product.link || '';
  const aiReason = cleanAiReason(
    product.suggested_for_wording || product.major_usp || product.description || ''
  );
  const topPriority = String(product.primary_severity || levels[0] || '').toLowerCase();
  const priority = priorityLabel(topPriority);
  const matchConditions = uniqueList(product.suggested_for_conditions || []);
  const targetConditions = uniqueList(
    matchConditions.length ? matchConditions : product.concerns_treated || product.conditions || product.concerns
  );
  const skinTypes = uniqueList(product.skin_types || []);
  const skinConcerns = uniqueList(product.skin_concerns || []);
  const keyClaims = uniqueList(product.key_claims || []);
  const benefitLine = String(product.benefit_line || '').trim() || (product.major_usp ? stripHtml(product.major_usp) : '');
  const tags = uniqueList(product.tags || product.routines || []);
  const timeSession = product.time_session || product.am_pm || '';
  const usage = String(product.how_to_use || product.usage || '').trim();
  const size = String(product.size || product.variant_title || '').trim();
  const gender = String(product.gender || '').trim();
  const inStock = product.in_stock !== false;
  const ratingAvg = product.rating_avg ?? product.rating;
  const ratingCount = Number(product.rating_count || 0);
  const reviews = (product.reviews || []).filter((review) => review?.body);
  const hasReviews = reviews.length > 0 || ratingCount > 0 || ratingAvg;
  const [showReviews, setShowReviews] = useState(false);
  const discount = Number(product.discount || 0);
  const savings = formatRoundedAmount(product.savings_amount);
  const showProductLinkCta = showProductLink && isValidProductUrl(link);
  const saleAmount = product.sale_price ?? product.amount ?? product.price ?? product.mrp;
  const mrpAmount = product.mrp ?? product.amount ?? product.price;

  return (
    <article
      ref={cardRef}
      className={`recommended-live-product-card ${className}`.trim()}
    >
      <div className="live-card-image-wrap">
        <ProductImageCarousel images={imageUrls} alt={name} />

        <div className="live-card-promo-badges">
          {discount > 0 ? <span className="live-card-promo-badge live-card-promo-save">Save {discount}%</span> : null}
          {product.is_new ? <span className="live-card-promo-badge live-card-promo-new">New</span> : null}
          {product.is_bestseller ? (
            <span className="live-card-promo-badge live-card-promo-bestseller">Bestseller</span>
          ) : null}
        </div>

        <div className="live-card-image-meta">
          <span className={`live-card-stock-badge ${inStock ? 'in-stock' : 'out-of-stock'}`}>
            {inStock ? 'In stock' : 'Out of stock'}
          </span>
          {size ? <span className="live-card-size-badge">{size}</span> : null}
        </div>

        {priority ? (
          <div className="live-card-severity-badges">
            <span className={`live-card-severity-badge ${severityClass(topPriority)}`}>
              {priority}
            </span>
          </div>
        ) : null}
      </div>

      <div className="live-card-body">
        {matchConditions.length > 0 ? (
          <div className="live-card-personal-match">
            <span className="live-card-personal-match-label">Matched for your skin</span>
            <p className="live-card-personal-match-text">{matchConditions.join(' · ')}</p>
          </div>
        ) : null}

        <div className="live-card-topline">
          <p className="live-card-type">{product.product_type || product.productType || 'Skincare'}</p>
          <div className="live-card-meta-inline">
            {product.brand ? <span className="live-card-brand">{product.brand}</span> : null}
            {gender ? <span className="live-card-gender">{gender}</span> : null}
          </div>
        </div>

        <h3 className="live-card-name">{name}</h3>

        {benefitLine ? <p className="live-card-benefit">{benefitLine}</p> : null}

        {hasReviews ? (
          <div className="live-card-reviews-block">
            <div className="live-card-reviews-summary">
              {ratingAvg ? <span className="live-card-review-stars">{renderStars(ratingAvg)}</span> : null}
              {ratingAvg ? <span className="live-card-review-avg">{Number(ratingAvg).toFixed(1)}</span> : null}
              {ratingCount > 0 ? (
                <span className="live-card-review-count">
                  {ratingCount} review{ratingCount === 1 ? '' : 's'}
                </span>
              ) : null}
              <button
                type="button"
                className="live-card-review-toggle"
                onClick={() => setShowReviews((open) => !open)}
              >
                {showReviews ? 'Hide reviews' : 'Click to view'}
              </button>
            </div>

            {showReviews && reviews.length > 0 ? (
              <div className="live-card-reviews-wrap">
                {reviews.slice(0, 2).map((review, index) => (
                  <div key={`${review.author}-${index}`} className="live-card-review-item">
                    <div className="live-card-review-item-top">
                      <span className="live-card-review-author">{review.author || 'Customer'}</span>
                      {review.rating ? (
                        <span className="live-card-review-item-stars">{renderStars(review.rating)}</span>
                      ) : null}
                    </div>
                    <p className="live-card-review-body">{review.body}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {showReviews && reviews.length === 0 ? (
              <p className="live-card-review-empty">No written reviews to preview yet.</p>
            ) : null}
          </div>
        ) : null}

        {keyClaims.length > 0 ? (
          <div className="live-card-chip-row">
            {keyClaims.map((claim) => (
              <span key={claim} className="live-card-chip live-card-chip-claim">
                {claim}
              </span>
            ))}
          </div>
        ) : null}

        {skinTypes.length > 0 ? (
          <div className="live-card-targets-wrap">
            <span className="live-card-match-label">Skin type</span>
            <div className="live-card-chip-row">
              {skinTypes.map((item) => (
                <span key={item} className="live-card-chip live-card-chip-skin-type">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {skinConcerns.length > 0 ? (
          <div className="live-card-targets-wrap">
            <span className="live-card-match-label">Concerns</span>
            <div className="live-card-chip-row">
              {skinConcerns.map((item) => (
                <span key={item} className="live-card-chip live-card-chip-concern">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {!matchConditions.length && targetConditions.length > 0 ? (
          <div className="live-card-targets-wrap">
            <span className="live-card-match-label">Targets</span>
            <div className="live-card-chip-row">
              {targetConditions.map((condition) => (
                <span key={condition} className="live-card-chip live-card-chip-target">
                  {condition}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {aiReason ? (
          <div className="live-card-ai-reason">
            <div className="live-card-ai-reason-label">Why AI recommends this</div>
            <p className="live-card-reason">{aiReason}</p>
          </div>
        ) : null}

        {matchedIngredients.length > 0 ? (
          <div className="live-card-matched-ingredients-wrap">
            <span className="live-card-match-label">Key actives for your concern</span>
            <p className="live-card-matched-ingredients">{matchedIngredients.join(' · ')}</p>
          </div>
        ) : null}

        {ingredients.length > 0 && matchedIngredients.length === 0 ? (
          <div className="live-card-ingredients-wrap">
            <span className="live-card-match-label">Key actives</span>
            <p className="live-card-ingredients">{ingredients.join(' · ')}</p>
          </div>
        ) : null}

        {(timeSession || usage) ? (
          <div className="live-card-usage-wrap">
            <span className="live-card-match-label">When to use</span>
            {timeSession ? <p className="live-card-time">{timeSession}</p> : null}
            {usage ? <p className="live-card-usage">{usage}</p> : null}
          </div>
        ) : null}

        {tags.length > 0 ? (
          <div className="live-card-tags-wrap">
            <span className="live-card-match-label">Highlights</span>
            <div className="live-card-chip-row">
              {tags.map((tag) => (
                <span key={tag} className="live-card-chip">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        <div className="live-card-footer">
          <div className="live-card-price-block">
            <span className="live-card-price">{formatPrice(saleAmount, product.price)}</span>
            {mrpAmount && saleAmount && Number(mrpAmount) > Number(saleAmount) ? (
              <>
                <span className="live-card-mrp">₹{formatRoundedAmount(mrpAmount)}</span>
                {discount > 0 ? <span className="live-card-discount-tag">-{discount}%</span> : null}
              </>
            ) : null}
          </div>
          {savings ? <p className="live-card-savings-line">You save ₹{savings}</p> : null}
        </div>

        {storeTrust.length > 0 ? (
          <div className="live-card-store-trust-row">
            {storeTrust.map((item) => (
              <span key={item} className="live-card-store-trust-badge">
                {item}
              </span>
            ))}
          </div>
        ) : null}

        <div className="live-card-cta">
          {showAddToCart && onAddToCart ? (
            <ProductCartCounter
              key={`counter-${productId}`}
              product={product}
              onAddToCart={(nextProduct, quantity) => onAddToCart(nextProduct, quantity, productId)}
              initialQuantity={cartQuantity}
            />
          ) : null}
          {showProductLinkCta ? (
            <button
              type="button"
              className="live-card-view-link"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewProduct) onViewProduct(product, link);
                else window.open(link, '_blank', 'noopener,noreferrer');
              }}
            >
              View product →
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}
